import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.jsx';
import './index.css';

const box = {
  font: '14px/1.5 system-ui, sans-serif', padding: 20, maxWidth: 640, margin: '40px auto', color: '#0e2733',
};
const pre = {
  whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f5f9fb', border: '1px solid #e0e8ec',
  borderRadius: 10, padding: 12, fontSize: 12, maxHeight: 220, overflow: 'auto',
};
const btn = { padding: '10px 16px', borderRadius: 10, border: 0, background: '#0e7490', color: '#fff', fontWeight: 600 };

// Catches render-time errors (e.g. a browser extension or auto-translate mutating the DOM)
// and offers a reload instead of leaving a blank page.
class ErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null, info: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { this.setState({ info }); console.error('DockDash crashed:', err, info); }
  render() {
    if (!this.state.err) return this.props.children;
    const e = this.state.err;
    // The message and the component stack are what actually identify the bug —
    // a minified frame list on its own is not enough to act on.
    const headline = `${e?.name || 'Error'}: ${e?.message || String(e)}`;
    const where = (this.state.info?.componentStack || '').trim().split('\n').slice(0, 6).join('\n');
    return (
      <div style={box}>
        <h2 style={{ margin: '0 0 6px' }}>Something went wrong</h2>
        <p style={{ color: '#5a7382', margin: '0 0 10px' }}>
          Reloading usually fixes it. If it keeps happening, copy everything in the box below and send it to me.
        </p>
        <pre style={{ ...pre, background: '#fdeceb', borderColor: '#f3c9c5', fontWeight: 600 }}>{headline}</pre>
        <button style={btn} onClick={() => location.reload()}>Reload DockDash</button>
        <details style={{ marginTop: 14 }} open>
          <summary style={{ color: '#5a7382', cursor: 'pointer' }}>Details to send</summary>
          <pre style={pre}>{`${headline}

WHERE:
${where || '(no component stack)'}

STACK:
${String(e?.stack || '(none)')}

${navigator.userAgent}`}</pre>
        </details>
      </div>
    );
  }
}

// If the app fails before React can mount at all, we still need to say something.
function showFatal(what, err) {
  const el = document.getElementById('root');
  if (!el || el.dataset.mounted === '1') return; // React is up; its boundary handles it
  const msg = (err && (err.stack || err.message)) || String(err || 'unknown error');
  const esc = (s) => s.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
  el.innerHTML = `
    <div style="font:14px/1.5 system-ui,sans-serif;padding:20px;max-width:640px;margin:40px auto;color:#0e2733">
      <h2 style="margin:0 0 6px">DockDash couldn't start</h2>
      <p style="color:#5a7382;margin:0 0 12px">${what}. Send me the details below and I can fix it.</p>
      <pre style="white-space:pre-wrap;word-break:break-word;background:#f5f9fb;border:1px solid #e0e8ec;border-radius:10px;padding:12px;font-size:12px">${esc(msg)}</pre>
      <p style="color:#8aa1ac;font-size:12px">${esc(navigator.userAgent)}</p>
      <button onclick="location.reload()" style="padding:10px 16px;border-radius:10px;border:0;background:#0e7490;color:#fff;font-weight:600">Reload</button>
    </div>`;
}

window.addEventListener('error', (e) => showFatal('A script error occurred', e.error || e.message));
window.addEventListener('unhandledrejection', (e) => showFatal('A request failed', e.reason));

try {
  const el = document.getElementById('root');
  createRoot(el).render(
    <ErrorBoundary><App /></ErrorBoundary>
  );
  el.dataset.mounted = '1';
} catch (err) {
  showFatal('The app failed to render', err);
}
